'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { AlertTriangle, CheckCircle2, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useSyncProgress } from '@/components/settings/hooks/use-sync-progress';
import { useSyncBannerStore } from '@/stores/sync-banner-store';
import type { SyncProvider } from '@/components/onboarding/sync-progress-screen';
import { formatEta } from '@/lib/onboarding/sync-types';

const PROVIDER_META: Record<SyncProvider, { label: string; color: string }> = {
  'microsoft-o365': { label: 'Microsoft 365', color: '#0078D4' },
  'google-workspace': { label: 'Google Workspace', color: '#4285F4' },
  ldap: { label: 'Active Directory', color: '#6B7280' },
  okta: { label: 'Okta', color: '#007DC1' },
};

/**
 * Surfaces where the admin sync banner must stay completely silent — no
 * render, no SSE poll. Login, 2FA enrollment, the impersonation-exchange
 * tab, the employee/recipient portal, the play + player training surfaces,
 * and public landing pages. Mirrors the non-admin subset of proxy.ts's
 * PUBLIC_PATHS. ( `/onboarding` is handled separately — it's authenticated,
 * and the full SyncProgressScreen already covers that case. )
 */
const PUBLIC_BANNER_BLOCKLIST = [
  '/login',
  '/admin/login',
  '/admin/setup-2fa',
  '/admin/verify-2fa',
  '/admin/impersonate',
  '/auth',
  '/accept-invite',
  '/access-denied',
  '/play',
  '/player',
  '/portal',
  '/certificates/verify',
];

/**
 * Persistent mini-banner that keeps the user informed when they navigate
 * away from the onboarding sync screen before the sync finishes. The BE
 * job keeps running regardless — this is purely a UI anchor so the user
 * doesn't lose track.
 *
 * Hidden while the user is ON /onboarding (the full SyncProgressScreen
 * covers that case) and hidden after the user explicitly dismisses the
 * current sync.
 */
export function SyncBackgroundBanner() {
  const pathname = usePathname();
  const domainId = useSyncBannerStore((s) => s.domainId);
  const provider = useSyncBannerStore((s) => s.provider);
  const dismissedDomainId = useSyncBannerStore((s) => s.dismissedDomainId);
  const clear = useSyncBannerStore((s) => s.clear);
  const dismiss = useSyncBannerStore((s) => s.dismiss);

  const onOnboarding = pathname?.startsWith('/onboarding') ?? false;
  const isDismissed = dismissedDomainId === domainId;

  // The banner store persists to localStorage (`hf-app:sync-banner`), so a
  // domainId from a prior sync survives logout and navigation. Without these
  // guards the banner flashes "Google Workspace syncing…" over the /login
  // form and fires `/v1/onboarding/sync-progress` from an UNAUTHENTICATED
  // page (401 → refresh → retry loop). The banner is an admin-shell-only
  // affordance: never render or poll on a public/non-admin surface, and never
  // without a live admin session token.
  const onPublicSurface =
    !pathname ||
    pathname === '/' ||
    PUBLIC_BANNER_BLOCKLIST.some((p) => pathname.startsWith(p));
  const hasAdminSession =
    typeof window !== 'undefined' &&
    !!localStorage.getItem('hf-app:access-token');

  // Subscription stays active even while dismissed so the terminal
  // toast still fires. The visual banner is hidden at render-time when
  // dismissed — the user still gets their "sync complete" toast.
  const shouldSubscribe =
    !!domainId && !onOnboarding && !onPublicSurface && hasAdminSession;

  const sync = useSyncProgress(shouldSubscribe ? domainId : null, {
    onTerminal: () => {
      // Fire a toast, let banner linger briefly with terminal status,
      // then auto-clear after 6s so it doesn't clutter the nav bar.
    },
  });

  const terminalToastFiredRef = useRef<string | null>(null);

  useEffect(() => {
    if (!domainId || !sync.isTerminal) return;
    if (terminalToastFiredRef.current === domainId) return;
    terminalToastFiredRef.current = domainId;

    const created = sync.progress?.stats.created ?? 0;
    if (sync.isSuccess && (sync.progress?.stats.failed ?? 0) === 0) {
      toast.success(
        `Sync complete — ${created.toLocaleString()} user${created === 1 ? '' : 's'} imported`,
      );
    } else if (sync.isSuccess) {
      toast.warning(
        `Sync finished with ${sync.progress?.stats.failed ?? 0} warning${(sync.progress?.stats.failed ?? 0) === 1 ? '' : 's'}`,
      );
    } else if (sync.isFailed) {
      toast.error('Sync failed — check Integrations for details');
    }

    const timer = setTimeout(() => clear(), 6000);
    return () => clearTimeout(timer);
  }, [
    domainId,
    sync.isTerminal,
    sync.isSuccess,
    sync.isFailed,
    sync.progress,
    clear,
  ]);

  // Banner stays mounted through terminal states. The 6s auto-clear
  // timer in the onTerminal useEffect removes it from the DOM so the
  // user sees the final outcome before it disappears.
  // Hide the visual banner when dismissed — but the subscription above
  // is still alive, so the terminal toast still fires.
  if (!provider || !shouldSubscribe || isDismissed) return null;

  const meta = PROVIDER_META[provider];
  const percentage = sync.percentage;
  const fetched = sync.progress?.stats.fetched ?? 0;
  const totalEstimate = sync.progress?.stats.totalEstimate;
  const eta = formatEta(sync.progress?.eta ?? null);

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'fixed top-4 right-4 z-50 flex w-full max-w-sm items-center gap-3 rounded-xl border bg-background p-3 pr-2 shadow-lg',
        sync.isFailed && 'border-rose-300 bg-rose-50 dark:bg-rose-950/40',
        sync.isSuccess && 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40',
      )}
    >
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-semibold text-white shadow-sm"
        style={{ backgroundColor: meta.color }}
      >
        {meta.label.charAt(0)}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {sync.isSuccess ? (
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
          ) : sync.isFailed ? (
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-rose-500" />
          ) : (
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
          )}
          <span className="truncate text-sm font-medium">
            {sync.isSuccess
              ? `${meta.label} synced`
              : sync.isFailed
                ? `${meta.label} sync failed`
                : `${meta.label} syncing…`}
          </span>
          <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">
            {sync.isSuccess || sync.isFailed ? '' : `${percentage}%`}
          </span>
        </div>

        {!sync.isSuccess && !sync.isFailed && (
          <>
            <Progress value={percentage} className="mt-1.5 h-1" />
            <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
              <span className="truncate">
                {fetched.toLocaleString()}
                {totalEstimate ? ` / ${totalEstimate.toLocaleString()}` : ''}{' '}
                users
              </span>
              {eta && <span className="truncate">{eta}</span>}
            </div>
          </>
        )}

        {(sync.isSuccess || sync.isFailed) && (
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {sync.progress?.message}
          </div>
        )}

        {!sync.isTerminal && (
          <Link
            // Standalone progress page (deploy-proof sync, 2026-07-10). The
            // old /onboarding target dead-ends after onboarding completes —
            // the proxy guard bounces it straight back to the dashboard.
            href={{
              pathname: '/recipients/sync-progress',
              query: { domainId, provider },
            }}
            className="mt-1 inline-block text-[11px] font-medium text-primary hover:underline"
          >
            View full progress →
          </Link>
        )}
      </div>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0 text-muted-foreground"
        aria-label="Dismiss"
        onClick={() => {
          if (sync.isTerminal) {
            clear();
          } else {
            dismiss();
          }
        }}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
